import { Dispatch, RefObject, SetStateAction } from 'react';
import { ScoreBreakdownPanel } from '@/components/ScoreBreakdown/ScoreBreakdownPanel';
import { PrevalenceBreakdownPanel } from '@/components/PrevalenceBreakdown/PrevalenceBreakdownPanel';
import EvidenceTimelinePanel from '@/components/EvidenceTimeline/EvidenceTimelinePanel';
import SideBarContent from '@/components/QueriedContent/SideBarContent';
import GeneInfoPanelContent from '@/components/GeneContent/GeneInfoPanelContent';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import { NAVBAR_HEIGHT_PX } from '@/router/AppRouter';
import { ScoreHistoryPanel } from './ScoreHistoryPanel';
import { ScoreHistoryPanelState, SHOW_HISTORIAL_SCORE } from '../utils/searchResultsHelpers';

const LEFT_PANEL_STYLE = (isDesktop: boolean) => ({
  top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
  height: isDesktop ? `calc(100vh - ${NAVBAR_HEIGHT_PX}px)` : '44vh',
  width: isDesktop ? 340 : '100vw',
  minWidth: isDesktop ? 340 : '100vw',
});

interface LeftPanelsProps {
  leftInfoPanelItem: any;
  leftSentencePanel: { content: string; allowCopy: boolean } | null;
  leftScoreHistoryPanel: ScoreHistoryPanelState | null;
  scoreBreakdownRow: any;
  prevalenceBreakdownRow: any;
  evidenceTimelineRow: any;
  sentenceToCopyRef: RefObject<HTMLDivElement | null>;
  sentenceCopied: boolean;
  isDesktop: boolean;
  view: any;
  onCloseInfoPanel: () => void;
  onCloseSentencePanel: () => void;
  onCloseScoreHistoryPanel: () => void;
  onCloseScoreBreakdown: () => void;
  onClosePrevalenceBreakdown: () => void;
  onCloseEvidenceTimeline: () => void;
  onCopySentence: () => void;
  onUpdateScoreHistoryPanel: Dispatch<SetStateAction<ScoreHistoryPanelState | null>>;
}

const ASIDE_CLASS =
  'fixed left-0 z-40 flex flex-col border-r border-gray-200 bg-white shadow-lg overflow-hidden';

export function LeftPanels({
  leftInfoPanelItem,
  leftSentencePanel,
  leftScoreHistoryPanel,
  scoreBreakdownRow,
  prevalenceBreakdownRow,
  evidenceTimelineRow,
  sentenceToCopyRef,
  sentenceCopied,
  isDesktop,
  view,
  onCloseInfoPanel,
  onCloseSentencePanel,
  onCloseScoreHistoryPanel,
  onCloseScoreBreakdown,
  onClosePrevalenceBreakdown,
  onCloseEvidenceTimeline,
  onCopySentence,
  onUpdateScoreHistoryPanel,
}: LeftPanelsProps) {
  const infoStyle = {
    top: isDesktop ? NAVBAR_HEIGHT_PX : '3.3rem',
    height: `calc(100vh - ${NAVBAR_HEIGHT_PX}px)`,
    width: isDesktop ? 340 : '100vw',
    minWidth: isDesktop ? 340 : '100vw',
  };
  const panelStyle = LEFT_PANEL_STYLE(isDesktop);

  return (
    <>
      {leftInfoPanelItem && (
        <aside role="complementary" aria-label="Item details" className={ASIDE_CLASS} style={infoStyle}>
          {leftInfoPanelItem?.entityView === 'GENES' ? (
            <GeneInfoPanelContent
              geneId={String(leftInfoPanelItem.id ?? leftInfoPanelItem.geneId ?? '')}
              geneSymbol={String(leftInfoPanelItem.symbol ?? leftInfoPanelItem.geneSymbol ?? '')}
              onClose={onCloseInfoPanel}
            />
          ) : (
            <SideBarContent
              selectedInfoItem={leftInfoPanelItem}
              view={view}
              showModalExample={false}
              setShowModalExample={() => {}}
              renderInPanel
              onPanelClose={onCloseInfoPanel}
            />
          )}
        </aside>
      )}

      {leftSentencePanel && !leftInfoPanelItem && (
        <aside role="complementary" aria-label="Sentence details" className={ASIDE_CLASS} style={panelStyle}>
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0">Sentence Details</h2>
            <button
              type="button"
              onClick={onCloseSentencePanel}
              className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 min-h-0">
            <div
              ref={sentenceToCopyRef}
              dangerouslySetInnerHTML={{ __html: leftSentencePanel.content }}
              className="text-secondary font-weight-bold"
            />
            {leftSentencePanel.allowCopy && (
              <div className="flex justify-start mt-3">
                <SharedButton size="xs" variant="secondary" onClick={onCopySentence} className="ml-0 mt-0">
                  {sentenceCopied ? (
                    <CheckIcon className="w-4 h-4 mr-1" strokeWidth={2} />
                  ) : (
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" strokeWidth={2} />
                  )}
                  {sentenceCopied ? ' Copied' : ' Copy sentence'}
                </SharedButton>
              </div>
            )}
          </div>
        </aside>
      )}

      {SHOW_HISTORIAL_SCORE && leftScoreHistoryPanel && !leftInfoPanelItem && !leftSentencePanel && (
        <aside role="complementary" aria-label="Score history" className={ASIDE_CLASS} style={panelStyle}>
          <ScoreHistoryPanel
            panel={leftScoreHistoryPanel}
            onUpdate={onUpdateScoreHistoryPanel}
            onClose={onCloseScoreHistoryPanel}
            isDesktop={isDesktop}
          />
        </aside>
      )}

      {scoreBreakdownRow && !leftInfoPanelItem && !leftSentencePanel && (
        <aside role="complementary" aria-label="Score breakdown" className={ASIDE_CLASS} style={panelStyle}>
          <ScoreBreakdownPanel rowData={scoreBreakdownRow} onClose={onCloseScoreBreakdown} />
        </aside>
      )}

      {prevalenceBreakdownRow && !leftInfoPanelItem && !leftSentencePanel && !scoreBreakdownRow && (
        <aside
          role="complementary"
          aria-label="Prevalence breakdown"
          className={ASIDE_CLASS}
          style={panelStyle}
        >
          <PrevalenceBreakdownPanel rowData={prevalenceBreakdownRow} onClose={onClosePrevalenceBreakdown} />
        </aside>
      )}

      {evidenceTimelineRow &&
        !leftInfoPanelItem &&
        !leftSentencePanel &&
        !scoreBreakdownRow &&
        !prevalenceBreakdownRow && (
          <aside
            role="complementary"
            aria-label="Evidence timeline"
            className={ASIDE_CLASS}
            style={panelStyle}
          >
            <EvidenceTimelinePanel rowData={evidenceTimelineRow} onClose={onCloseEvidenceTimeline} />
          </aside>
        )}
    </>
  );
}
